/**
 * Google Analytics 4 for the demo client: an in-memory connection per tenant
 * (this file) and deterministic traffic (`@/mock/ga4/mockTrafficReport`, which the store
 * composes), so the Integrations card and the Traffic page work with no backend.
 *
 * Three tenants start in the three states the real API can answer with: the 676
 * demo tenant is connected (two properties, 90 days of traffic, one of them
 * stale); Northfield's environment has no service account (not configured);
 * every other tenant is configured but has no property yet. Connecting one of a
 * few reserved property ids reproduces Google's refusals, so each error the
 * page explains can be seen without a Google account.
 */

import type { CampaignWebsite } from '@/api/campaigns'
import { Ga4OwnershipError } from '@/api/ga4'
import type {
  Ga4ImportRun,
  Ga4ImportSkip,
  Ga4Integration,
  Ga4Property,
  Ga4StreamCheck,
  Ga4TrafficQuery,
  Ga4TrafficReport,
} from '@/api/types'
import { ApiError } from '@/lib/api'
import { toDateKey } from '@/lib/filters'
import { DEMO_REFUSALS } from '@/mock/ga4/mockRefusals'
import { DEFAULT_BASE_SESSIONS, trafficCsv } from '@/mock/ga4/mockTraffic'
import { buildTrafficReport } from '@/mock/ga4/mockTrafficReport'
import { isLive } from './status'

type DemoStart = 'connected' | 'not_connected' | 'not_configured'

const DEMO_START: Record<string, DemoStart> = {
  demo_676: 'connected',
  demo_northfield: 'not_configured',
}

const SERVICE_ACCOUNT_EMAIL = 'sa-cinsights-ga4-demo@tmt-insights-demo.iam.gserviceaccount.com'
const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS
const MANUAL_IMPORT_INTERVAL_MS = 10 * 60 * 1000

/** Property ids that make `connect` fail the way Google (and the ownership check) do. */
export const DEMO_UNGRANTED_PROPERTY = '111111111'
export const DEMO_MISSING_PROPERTY = '222222222'
export const DEMO_OWNED_ELSEWHERE_PROPERTY = '333333333'

/** `DEMO_OWNED_ELSEWHERE_PROPERTY`'s web data stream: nobody's website on this account. */
const DEMO_ELSEWHERE_HOST = 'rival-competitions.example'

function siteHost(origin: string): string | null {
  try {
    return new URL(origin).hostname.toLowerCase().replace(/^www\./, '')
  } catch {
    return null
  }
}

export class MockGa4Store {
  private readonly configured: boolean
  private properties: Ga4Property[] = []
  private readonly baseSessions = new Map<string, number>()

  constructor(
    tenantId: string,
    private readonly websites: () => CampaignWebsite[],
    private readonly now: () => Date,
  ) {
    const start = DEMO_START[tenantId] ?? 'not_connected'
    this.configured = start !== 'not_configured'
    if (start === 'connected') this.seed()
  }

  overview(): Ga4Integration {
    return {
      configured: this.configured,
      serviceAccountEmail: this.configured ? SERVICE_ACCOUNT_EMAIL : null,
      configurationError: null,
      properties: this.properties.map((property) => ({ ...property })),
    }
  }

  connect(rawId: string, websiteId?: string): Ga4Property {
    this.requireConfigured()
    const propertyId = this.normalisedPropertyId(rawId)
    this.refuseKnownDemoRefusal(propertyId)
    if (propertyId === DEMO_OWNED_ELSEWHERE_PROPERTY) {
      const websiteHosts = this.ownWebsiteHosts()
      throw new Ga4OwnershipError(
        422,
        'validation_error',
        DEMO_REFUSALS.ownershipNotProven(propertyId),
        undefined,
        [DEMO_ELSEWHERE_HOST],
        websiteHosts,
      )
    }
    if (this.properties.some((property) => property.propertyId === propertyId && isLive(property))) {
      throw new ApiError(409, 'conflict', DEMO_REFUSALS.alreadyConnected(propertyId))
    }
    if (websiteId && !this.websites().some((website) => website.id === websiteId)) {
      throw new ApiError(404, 'tenant_scope_error', 'resource not found in this tenant')
    }
    const now = this.now().toISOString()
    const property: Ga4Property = {
      ga4PropertyId: `ga4p_demo_${this.properties.length + 1}`,
      propertyId,
      displayName: `Demo property ${propertyId}`,
      websiteId: websiteId ?? null,
      timezone: 'Europe/London',
      currency: 'GBP',
      status: 'connected',
      connectedAt: now,
      lastImportAt: now,
      lastImportStatus: 'succeeded',
      lastError: null,
      stale: false,
      providerLag: false,
      nextManualImportAt: null,
      rowsLast30Days: 2400,
    }
    this.properties = [...this.properties, property]
    return { ...property }
  }

  disconnect(ga4PropertyId: string): Ga4Property {
    this.requireConfigured()
    const property = this.get(ga4PropertyId)
    property.status = 'disconnected'
    return { ...property }
  }

  /** Run import now for one property. */
  runNow(ga4PropertyId: string): Ga4Property[]
  /** Run import now for every live property; each has its own ten-minute limit. */
  runNow(): Ga4ImportRun
  runNow(ga4PropertyId?: string): Ga4Property[] | Ga4ImportRun {
    this.requireConfigured()
    const now = this.now()
    if (ga4PropertyId) {
      const property = this.get(ga4PropertyId)
      if (!isLive(property)) {
        throw new ApiError(409, 'conflict', 'a disconnected GA4 property is not imported; connect it again')
      }
      if (this.coolingDown(property, now)) {
        throw new ApiError(429, 'rate_limited', 'an import of this property was requested less than ten minutes ago')
      }
      this.importNow(property, now)
      return [{ ...property }]
    }
    const live = this.properties.filter(isLive)
    if (live.length === 0) {
      throw new ApiError(422, 'validation_error', 'no GA4 property is connected')
    }
    const skipped: Ga4ImportSkip[] = []
    let ran = 0
    for (const property of live) {
      if (this.coolingDown(property, now)) {
        const retryAfterSeconds = Math.max(
          1,
          Math.ceil((new Date(property.nextManualImportAt as string).getTime() - now.getTime()) / 1000),
        )
        skipped.push({ ga4PropertyId: property.ga4PropertyId, retryAfterSeconds })
        continue
      }
      this.importNow(property, now)
      ran += 1
    }
    if (ran === 0) {
      throw new ApiError(429, 'rate_limited', 'an import of this property was requested less than ten minutes ago')
    }
    return { integration: this.overview(), ran, skipped }
  }

  /** Read-only, before connecting: the reserved demo properties reproduce Google's answers. */
  checkStreams(rawId: string): Ga4StreamCheck {
    this.requireConfigured()
    const propertyId = this.normalisedPropertyId(rawId)
    this.refuseKnownDemoRefusal(propertyId)
    const websiteHosts = this.ownWebsiteHosts()
    const streamHosts = propertyId === DEMO_OWNED_ELSEWHERE_PROPERTY ? [DEMO_ELSEWHERE_HOST] : websiteHosts
    const matches = streamHosts.filter((host) => websiteHosts.includes(host)).sort()
    return { propertyId, streamHosts, websiteHosts, matches, ownershipProvable: matches.length > 0 }
  }

  /** `123456789` or `properties/123456789`, format-checked; the numeric id either way. */
  private normalisedPropertyId(rawId: string): string {
    const propertyId = rawId.trim().replace(/^properties\//, '')
    if (!/^[0-9]{1,20}$/.test(propertyId)) {
      throw new ApiError(422, 'validation_error', 'property_id is the numeric GA4 property id (Admin > Property settings), e.g. 123456789')
    }
    return propertyId
  }

  /**
   * The reserved demo ids that fail the same way for `connect()` and `checkStreams()`:
   * an ungranted or unknown property. `DEMO_OWNED_ELSEWHERE_PROPERTY` is not here — the
   * two callers word that refusal differently (a stored `Ga4OwnershipError` vs. a
   * computed stream-host mismatch), so each keeps its own branch for it.
   */
  private refuseKnownDemoRefusal(propertyId: string): void {
    if (propertyId === DEMO_UNGRANTED_PROPERTY) {
      throw new ApiError(422, 'validation_error', DEMO_REFUSALS.grantMissing(SERVICE_ACCOUNT_EMAIL, propertyId))
    }
    if (propertyId === DEMO_MISSING_PROPERTY) {
      throw new ApiError(422, 'validation_error', DEMO_REFUSALS.propertyNotFound(propertyId))
    }
  }

  private ownWebsiteHosts(): string[] {
    const hosts = this.websites()
      .map((website) => siteHost(website.origin))
      .filter((host): host is string => host !== null)
    return Array.from(new Set(hosts)).sort()
  }

  private importNow(property: Ga4Property, now: Date): void {
    Object.assign(property, {
      status: 'connected',
      lastImportAt: now.toISOString(),
      lastImportStatus: 'succeeded',
      lastError: null,
      stale: false,
      providerLag: false,
      nextManualImportAt: new Date(now.getTime() + MANUAL_IMPORT_INTERVAL_MS).toISOString(),
    })
  }

  traffic(query: Ga4TrafficQuery): Ga4TrafficReport {
    return buildTrafficReport({
      properties: this.trafficProperties(query.propertyId),
      query,
      today: toDateKey(this.now()),
      baseSessions: (property) => this.baseSessions.get(property.ga4PropertyId) ?? DEFAULT_BASE_SESSIONS,
    })
  }

  trafficCsv(query: Ga4TrafficQuery): Blob {
    return trafficCsv(this.traffic(query))
  }

  private seed(): void {
    const now = this.now()
    const production = this.websites().find((website) => website.environment === 'production')
    const base = { timezone: 'Europe/London', currency: 'GBP', status: 'connected', lastImportStatus: 'succeeded', lastError: null } as const
    this.properties = [
      {
        ...base,
        ga4PropertyId: 'ga4p_demo_1',
        propertyId: '300000001',
        displayName: 'Main website',
        websiteId: production?.id ?? null,
        connectedAt: new Date(now.getTime() - 40 * DAY_MS).toISOString(),
        lastImportAt: new Date(now.getTime() - 5 * HOUR_MS).toISOString(),
        stale: false,
        providerLag: false,
        nextManualImportAt: null,
        rowsLast30Days: 5200,
      },
      {
        ...base,
        ga4PropertyId: 'ga4p_demo_2',
        propertyId: '300000002',
        displayName: 'Prize draws microsite',
        websiteId: null,
        connectedAt: new Date(now.getTime() - 40 * DAY_MS).toISOString(),
        lastImportAt: new Date(now.getTime() - 3 * DAY_MS).toISOString(),
        stale: true,
        providerLag: true,
        nextManualImportAt: null,
        rowsLast30Days: 610,
      },
    ]
    this.baseSessions.set('ga4p_demo_1', 900).set('ga4p_demo_2', 220)
  }

  private get(ga4PropertyId: string): Ga4Property {
    const property = this.properties.find((candidate) => candidate.ga4PropertyId === ga4PropertyId)
    if (!property) throw new ApiError(404, 'tenant_scope_error', 'resource not found in this tenant')
    return property
  }

  private coolingDown(property: Ga4Property, now: Date): boolean {
    return property.nextManualImportAt !== null && new Date(property.nextManualImportAt).getTime() > now.getTime()
  }

  /** Every live property, or the one named (a disconnected property's traffic is kept, so it can be named). */
  private trafficProperties(propertyId: string | undefined): Ga4Property[] {
    if (!propertyId) return this.properties.filter(isLive)
    const property = this.properties.find((candidate) => candidate.propertyId === propertyId)
    if (!property) throw new ApiError(404, 'tenant_scope_error', 'resource not found in this tenant')
    return [property]
  }

  private requireConfigured(): void {
    if (!this.configured) {
      throw new ApiError(
        503,
        'provider_unavailable',
        DEMO_REFUSALS.notConfigured('COMPETITION_INSIGHTS_GA4_SERVICE_ACCOUNT_JSON'),
      )
    }
  }
}

/** One store per demo client and tenant, so a session's changes survive navigation. */
const stores = new WeakMap<object, Map<string, MockGa4Store>>()

export function ga4StoreFor(owner: object, tenantId: string, websites: () => CampaignWebsite[], now: () => Date): MockGa4Store {
  let byTenant = stores.get(owner)
  if (!byTenant) {
    byTenant = new Map()
    stores.set(owner, byTenant)
  }
  let store = byTenant.get(tenantId)
  if (!store) {
    store = new MockGa4Store(tenantId, websites, now)
    byTenant.set(tenantId, store)
  }
  return store
}
